import { Student } from '@/common/types/student';
import { setAudioVolume } from '@/common/utils/audio';
import {
  authenticateStudent,
  enforceAutoLogout,
  handleStudentLogout,
} from '@/common/utils/auth';
import {
  clearMixpanelUser,
  identifyMixpanelUser,
} from '@/common/utils/mixpanel';
import { getUpdatedStudent } from '@/common/utils/student';
import api from '@/db/apiClient';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import Assessment from '@/game/GameComponents/PhaserGame/Scenes/Assessment';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { GridLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/GridLayoutGroup';
import {
  getAnchorPlugin,
  waitForMilliseconds,
  waitUntil,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { DEFAULT_CURSOR_PATH } from '@/game/GameComponents/Utils/GameSpriteAssetsMap';
import ConfirmButtonLarge from '@/game/prefabs/GeneralUI/Buttons/ConfirmButtonLarge';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import DenyButtonLarge from '@/game/prefabs/GeneralUI/Buttons/DenyButtonLarge';
import LevelMap from '@/game/scenes/LevelMap';
import Login from '@/game/scenes/Login/Login';
import { PhaserLoginForm } from '@/game/scenes/Login/PhaserLoginForm';
import MobileOverlay from '@/game/scenes/MobileOverlay';

import Paywall from '@/game/scenes/Paywall';
import Preloader from '@/game/scenes/Preloader';
import { onAuthStateChanged } from 'firebase/auth';

export class LoginController {
  public constructor(private login: Login) {}

  private loginForm?: PhaserLoginForm;
  private authUnsub: (() => void) | null = null;
  currentView: string = '';
  playButtonsGrid?: GridLayoutGroup;
  playButtonsGrid_m?: GridLayoutGroup;

  init() {
    const { login } = this;
    // Initialize breakpoints.
    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: login,
      desktopView: login.desktopView,
      mobileView: login.mobileView,
    });

    // Initialize desktop controls for login screen.
    this.getResponsiveElements('loginButton').forEach(
      (button: ConfirmButtonLarge) => {
        button.controller.setSubmissionCallback(async () => {
          this.handleLoginSubmitted();
        });
      },
    );

    this.getResponsiveElements('resumeButton').forEach(
      (button: ConfirmButtonLarge) => {
        button.controller.setSubmissionCallback(async () => {
          this.handleGameResumed();
        });
      },
    );

    this.getResponsiveElements('quitToMenuButton').forEach(
      (button: DenyButtonLarge) => {
        button.controller.setSubmissionCallback(async () => {
          EventBus.emit(GAME_EVENTS.USER_LOGOUT);
        });
      },
    );

    this.getResponsiveElements('logoutButton').forEach(
      (button: DenyButtonLarge) => {
        button.controller.setSubmissionCallback(async () => {
          this.logout();
        });
      },
    );

    this.getResponsiveElements('startAssessmentButton').forEach(
      (button: ConfirmButtonLarge) => {
        button.controller.setSubmissionCallback(async () => {
          this.handleStartAssessment();
        });
      },
    );

    this.getResponsiveElements('startCustomLevelButton').forEach(
      (button: ConfirmButtonLarge) => {
        button.controller.setSubmissionCallback(async () => {
          this.handleStartCustomLevel();
        });
      },
    );

    this.playButtonsGrid = new GridLayoutGroup({
      scene: this.login,
      elements: [
        login.startCustomLevelButton,
        login.startAssessmentButton,
        login.resumeButton,
        login.logoutButton,
      ],
      hPadding: 0,
      vPadding: 56,
      columns: 1,
    });
    login.playButtonsContainer.add(this.playButtonsGrid);

    this.playButtonsGrid_m = new GridLayoutGroup({
      scene: this.login,
      elements: [
        login.startCustomLevelButton_m,
        login.startAssessmentButton_m,
        login.resumeButton_m,
        login.logoutButton_m,
      ],
      hPadding: 0,
      vPadding: 56,
      columns: 1,
    });
    login.playButtonsContainer_m.add(this.playButtonsGrid_m);

    login.helpButton.setSubmissionCallback(() => {
      login.helpTooltip.setVisible(!login.helpTooltip.visible);
    });

    login.helpTooltipBacking.setInteractive({
      cursor: `${DEFAULT_CURSOR_PATH}, auto`,
    });

    // Hide the help tooltip when the user clicks anywhere outside of it.
    login.input.on('pointerup', (_: any, currentlyOver: any) => {
      if (
        !currentlyOver.find(
          (item: any) =>
            item === login.helpTooltipBacking || item === login.helpButton,
        )
      ) {
        login.helpTooltip.setVisible(false);
      }
    });

    this.loginForm = new PhaserLoginForm(login);
    this.loginForm.handleLoginSubmitted = () => this.handleLoginSubmitted();
    this.getResponsiveElements('inputPlaceholder_username').forEach(
      (input: any) => {
        input.setVisible(false);
      },
    );
    this.getResponsiveElements('inputPlaceholder_password').forEach(
      (input: any) => {
        input.setVisible(false);
      },
    );
    login.helpTooltip.setVisible(false);
    this.refreshTrialTimer();

    // Responsiveness.
    this.initializeResponsiveness();

    // Add resize handler
    ResponsivenessManager.InitializeResizeHandler(
      login,
      () => this.handleResize(),
      this,
    );

    login.events.on('destroy', this.destroy, this);
    login.events.on('shutdown', this.destroy, this);
    login.events.on('resume', this.resume, this);

    // Create the paywall.
    getSceneManager()?.launchSubScene('Login', 'Paywall', Paywall, 'ui');

    // Create the mobile overlay.
    getSceneManager()?.launchScene('MobileOverlay', MobileOverlay, {
      type: 'ui',
    });

    this.initializeLogin();
  }

  getResponsiveElement(componentKey: keyof Login) {
    return ResponsivenessManager.GetResponsiveComponentFromScene<Login>(
      this.login,
      componentKey,
      this.getCurrentView(),
    );
  }

  getResponsiveElements(key: keyof Login) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromScene<Login>(
      this.login,
      key,
    );
  }

  update() {
    this.loginForm?.update();
  }

  // Deliberately excludes tablet view.
  getCurrentView() {
    const globalView = ResponsivenessManager.GetCurrentGlobalViewForGame(
      this.login,
    );
    return globalView === 'tablet' ? 'desktop' : globalView;
  }

  async initializeLogin() {
    this.setView('loading');
    this.authUnsub = onAuthStateChanged(
      api.asStudent.auth,
      async (authUser) => {
        if (authUser) {
          const didAutoLogout = enforceAutoLogout('student', () =>
            this.logout(),
          );
          if (!didAutoLogout) {
            try {
              await this.loadStudent(authUser.uid);
              await waitUntil(this.login.time, () => Paywall.hasCheckedPaywall);
              this.setLoginInteractive(true);
            } catch (e) {
              console.error('Error loading student:', e);
            }
          }
        } else {
          clearMixpanelUser('student');
        }
        this.setupView();
      },
    );
  }

  resume() {
    this.setLoginInteractive(true);
    enforceAutoLogout('student', () => this.logout());
    this.setupView();
  }

  async setupView() {
    if (getStudent().id) {
      this.setView('resume');
    } else {
      this.setView('login');
    }
  }

  setView(view: 'login' | 'resume' | 'loading') {
    this.currentView = view;
    this.getResponsiveElements('inputView').forEach(
      (inputView: Phaser.GameObjects.Container) => {
        inputView.setVisible(view === 'login');
      },
    );
    this.refreshFormVisibility(view);
    this.getResponsiveElements('resumeView').forEach(
      (resumeView: Phaser.GameObjects.Container) => {
        resumeView.setVisible(view === 'resume');
      },
    );
    this.getResponsiveElements('loadingView').forEach(
      (loadingView: Phaser.GameObjects.Container) => {
        loadingView.setVisible(view === 'loading');
      },
    );
    this.getResponsiveElements('invalidLoginText').forEach(
      (text: Phaser.GameObjects.BitmapText) => {
        text.setVisible(false);
      },
    );
    switch (view) {
      case 'resume':
        const student = getStudent();
        if (!student?.id) {
          console.error('No student id found');
          return;
        }
        this.getResponsiveElements('loggedInAsText').forEach(
          (text: Phaser.GameObjects.BitmapText) => {
            text.setText(
              `Logged in as ${student.studentFirstName} ${student.studentSurname}`,
            );
          },
        );
        const resumeText = student.onboardingCompleted
          ? 'Continue'
          : 'New Game';
        const resumeButtons = this.getResponsiveElements('resumeButton');
        resumeButtons.forEach((button: DefaultButton) => {
          button.controller.setText(resumeText);
        });
        const assessmentExists = !!getStudent().assessmentId;
        this.getResponsiveElements('startAssessmentButton').forEach(
          (startAssessmentButton: DefaultButton) => {
            startAssessmentButton.setVisible(assessmentExists);
          },
        );

        const customLevelExists = !!getStudent().customLevelId;
        this.getResponsiveElements('startCustomLevelButton').forEach(
          (startCustomLevelButton: DefaultButton) => {
            startCustomLevelButton.setVisible(customLevelExists);
          },
        );

        this.playButtonsGrid?.updateLayout();
        this.playButtonsGrid_m?.updateLayout();
        break;
    }
    this.refreshTrialTimer();
  }

  async loadStudent(studentId: string) {
    // Load the student in from DB.
    let student = await api.asStudent.getStudent(studentId);

    // Load the organisation in from DB.
    const organisationId = student.organisationId;
    if (!organisationId) {
      throw new Error('not-found');
    }

    // Update the student to make sure they're on the right version & have right version of scope.
    student = await getUpdatedStudent(student, api.asStudent);

    // Load the student into game state.
    GameInstance.instance.student = student;

    // Identify the student in Mixpanel
    identifyMixpanelUser('student', student.id, student.organisationId);

    // Check if the student has a custom level.
    if (student.customLevelId) {
      const customLevel = await api.asStudent.getCustomLevel(
        student.customLevelId,
      );
      if (customLevel) {
        GameInstance.instance.customLevel = customLevel;
      }
    }

    // Check if the student has an assessment.
    if (student.assessmentId) {
      const assessment = await api.asStudent.getAssessment(
        student.assessmentId,
      );
      if (
        assessment &&
        Object.entries(assessment.students).some(
          ([id, s]) => id === studentId && !s.completedAt,
        )
      ) {
        // assessment exists, this student is assigned to it and they haven't completed it
        GameInstance.instance.assessment = assessment;
      }
    }

    // Update student's last login to be now.
    GameInstance.instance.updateStudent({
      lastLogin: Date.now(),
    });

    // Load student audio settings. MAYBE GET RID OF LATER
    const audioSettings = student.settings.audio;
    if (audioSettings) {
      setAudioVolume({
        masterVolume: audioSettings.masterVolume,
        sfxVolume: audioSettings.sfxVolume,
        instructionsVolume: audioSettings.instructionsVolume,
      });
    }

    EventBus.emit(GAME_EVENTS.USER_LOADED, studentId);
  }

  refreshFormVisibility(view: string) {
    const { login } = this;
    this.loginForm?.loginForm_d.setVisible(
      view === 'login' && !ResponsivenessManager.IsMobileDevice(login),
    );
    this.loginForm?.loginForm_m.setVisible(
      view === 'login' && ResponsivenessManager.IsMobileDevice(login),
    );
    this.loginForm?.handleResize();
  }

  async logout() {
    await handleStudentLogout();

    // Clear the student from game state
    GameInstance.instance.student = {} as Student;

    this.setView('login');
  }

  setLoginInteractive(isInteractive: boolean) {
    const { login } = this;
    this.getResponsiveElements('resumeButton').forEach(
      (resumeButton: ConfirmButtonLarge) => {
        resumeButton.controller.makeInteractive(isInteractive);
      },
    );
    this.getResponsiveElements('startAssessmentButton').forEach(
      (startAssessmentButton: ConfirmButtonLarge) => {
        startAssessmentButton.controller.makeInteractive(isInteractive);
      },
    );
    this.getResponsiveElements('startCustomLevelButton').forEach(
      (startCustomLevelButton: ConfirmButtonLarge) => {
        startCustomLevelButton.controller.makeInteractive(isInteractive);
      },
    );
    this.getResponsiveElements('logoutButton').forEach(
      (logoutButton: ConfirmButtonLarge) => {
        logoutButton.controller.makeInteractive(isInteractive);
      },
    );
    this.getResponsiveElements('loginButton').forEach(
      (loginButton: ConfirmButtonLarge) => {
        loginButton.controller.makeInteractive(isInteractive);
      },
    );
    this.getResponsiveElements('quitToMenuButton').forEach(
      (quitToMenuButton: DenyButtonLarge) => {
        quitToMenuButton.controller.makeInteractive(isInteractive);
      },
    );
    login.input.setDefaultCursor(
      isInteractive ? `${DEFAULT_CURSOR_PATH}, auto` : `none`,
    );
    const currentView =
      ResponsivenessManager.GetCurrentGlobalViewForGame(login);
    login.loadingCursor.setVisible(!isInteractive && currentView === 'desktop');
  }

  async startGame(sceneToStart: string, sceneData?: any) {
    // Disable interactivity.
    this.setLoginInteractive(false);

    // Student is in state, reload the student from the DB.
    const student = getStudent();
    if (!student?.id) {
      console.error('No student id found');
      return;
    }
    try {
      await this.loadStudent(student.id);
    } catch (e) {
      console.error('Error loading student:', e);
      return;
    }
    // Start the session.
    try {
      const newSession = await api.asStudent.createSession(
        student.organisationId,
        student.id,
      );
      GameInstance.instance.session.currentSession = newSession;
    } catch (error) {
      console.error('Failed to start session', error);
    }

    EventBus.emit(GAME_EVENTS.START_GAME);
    getSceneManager()?.launchScene('Preloader', Preloader, {
      type: 'gameplay',
      data: { sceneToStart: sceneToStart, sceneData: sceneData },
    });
  }

  async refreshTrialTimer() {
    // Trial timer.
    const { trialTimer } = this.login;
    const student = getStudent();
    if (!student.id) {
      // nothing to show
      trialTimer.setVisible(false);
      return;
    }

    try {
      const organisation = await api.asStudent.getOrganisation(
        student.organisationId,
      );
      if (!organisation) {
        trialTimer.setVisible(false);
        console.error('No organisation found');
        return;
      }
      trialTimer.setVisible(organisation.license?.plan === 'trial');
      trialTimer.update();
    } catch (e) {
      console.error('Error refreshing trial timer:', e);
      trialTimer.setVisible(false);
    }
  }

  initializeResponsiveness() {
    const { login } = this;
    const anchor = getAnchorPlugin(login);
    if (!anchor) return;

    anchor.add(login.mainContainer, {
      left: '0%+256',
      centerY: '50%',
    });
    anchor.add(login.mainContainer_m, {
      centerX: '50%',
      centerY: '50%',
      width: '100%',
    });

    anchor.add(login.bgContainer, {
      centerX: '50%',
      centerY: '50%',
    });

    anchor.add(login.mentor, {
      right: '90%',
      centerY: '100%+128',
    });

    anchor.add(login.starPatterns, {
      bottom: '100%',
      centerX: '50%',
    });
    anchor.add(login.starPatterns_m, {
      bottom: '100%',
      centerX: '50%',
    });

    anchor.add(login.trialTimer, {
      right: '100%-64',
    });

    anchor.add(login.helpContainer, {
      right: '100%',
      top: '0%',
    });
  }

  async handleResize() {
    const { login } = this;
    await waitForMilliseconds(login.time, 2);
    if (!login || !login.scene.isActive()) return;
    const anchor = getAnchorPlugin(login);
    const mobileBreakpoint = 640;
    // Toggle greenleaf at small screen size.
    login.mentor.setVisible(window.innerWidth > mobileBreakpoint);
    if (window.innerWidth > mobileBreakpoint) {
      anchor.add(login.mainContainer, {
        left: '0%+256',
        centerY: '50%',
      });
    } else {
      anchor.add(login.mainContainer, {
        centerX: '50%',
        centerY: '50%',
      });
    }
    this.refreshFormVisibility(this.currentView);
    this.loginForm?.handleResize();
  }

  async handleLoginSubmitted() {
    const { login } = this;
    const { invalidLoginText } = login;
    // Get username
    const usernameInput = this.loginForm?.getUsernameInput();
    const username = usernameInput ?? '';
    if (!username) {
      invalidLoginText.setText('Please enter a username');
      invalidLoginText.setVisible(true);
      return;
    }

    // Get password
    const passwordInput = this.loginForm?.getPasswordInput();
    const password = passwordInput ?? '';
    if (!password) {
      this.getResponsiveElements('invalidLoginText').forEach(
        (text: Phaser.GameObjects.BitmapText) => {
          text.setText('Please enter a password');
          text.setVisible(true);
        },
      );
      return;
    }

    this.setLoginInteractive(false);
    // Try logging in
    if (await authenticateStudent(username, password)) {
      this.setupView();
    } else {
      this.setLoginInteractive(true);
      this.getResponsiveElements('invalidLoginText').forEach(
        (invalidLoginText: Phaser.GameObjects.BitmapText) => {
          invalidLoginText.setText('Username or password is incorrect');
          invalidLoginText.setVisible(true);
        },
      );
    }
  }

  /*
  Calls the function to start the preloader and provides it with
  the next scene to load. This will force the user to first go
  through the character creation scene if they are not onboarded.
  */
  startGameWithOnboardingCheck(
    nextSceneKey: string,
    nextSceneType: typeof Phaser.Scene,
  ) {
    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return;
    }

    // if (ResponsivenessManager.IsMobileDevice(this.login, true)) {
    //   // Put the game into fullscreen.
    //   this.login.scale.startFullscreen();
    // }

    const targetScene = student.onboardingCompleted
      ? nextSceneKey
      : 'CharacterCreation';

    if (targetScene === 'CharacterCreation') {
      // Pass into the character creation scene the next scene to load after it.
      this.startGame(targetScene, { nextSceneKey, nextSceneType });
    } else {
      this.startGame(targetScene);
    }
  }

  handleGameResumed() {
    this.startGameWithOnboardingCheck('LevelMap', LevelMap);
  }

  handleStartAssessment() {
    this.startGameWithOnboardingCheck('Assessment', Assessment);
  }

  async handleStartCustomLevel() {
    GameInstance.instance.updateGeneralGameState({
      optedIntoCustomLevel: true,
    });
    this.startGameWithOnboardingCheck('LevelMap', LevelMap);
  }

  destroy() {
    if (this.authUnsub) this.authUnsub();
    const { login } = this;
    login.events.off('destroy', this.destroy, this);
    login.events.off('shutdown', this.destroy, this);
  }
}
